import {
  readDesktopCredential,
  clearDesktopCredential,
  persistDesktopCredential,
} from "./desktop-credentials.js";
// Silent token self-heal for the guest WebSocket (CYBORG-87 — the web/desktop
// mirror of the CLI's `auth-refresh.ts` + `relay-auth.ts`).
//
// A JWT-secret rotation on the relay (CYBORG-55) — or an ordinary access-token
// expiry — silently invalidates the token stored in the browser's session: the
// signature no longer verifies, so on the next (re)connect the relay answers
// `cyborg:auth` with a `cyborg:error` "unauthorized" AND closes the guest socket
// with `4003 unauthorized` (see relay-standalone.ts). The CLI self-heals; the UI
// did not — it just reconnect-looped forever against the dead token (a silent
// session death that looked like a permanent spinner / dead app).
//
// The fix mirrors the CLI exactly: trade the durable REFRESH token (issued by
// the relay alongside every session, signed with a separately-pinned secret so
// it outlives the rotation) at `POST /api/auth/refresh` for a fresh access
// token, then reconnect + re-auth transparently. Exactly ONE refresh attempt per
// rejection episode (never loops). On a dead refresh token (401/403) we clear it
// and surface an actionable re-login instead of an infinite spinner.
//
// These helpers are deliberately transport-agnostic so they can be unit-tested
// with a fake fetch (mirroring the CLI's injected-deps style). The WS wiring that
// consumes them lives in `client.ts` (the close-4003 handler) + `app.svelte.ts`.

import { timeoutSignal } from "./abort-signal.js";

// localStorage key for the durable refresh token (web/desktop/mobile all share
// the same ws-client, so this one store covers every shell; a native keychain is
// a v1-acceptable follow-up — see the PR body).
const REFRESH_TOKEN_KEY = "cyborg7-refresh-token";

/** Persist the durable refresh token issued by login/register/refresh. */
export function saveRefreshToken(token: string): void {
  try {
    if (typeof localStorage === "undefined") return;
    if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch {
    // best-effort: storage-disabled just means the next rotation won't self-heal.
  }
}

/** Load the stored refresh token, or null when absent / storage unavailable. */
export function loadRefreshToken(): string | null {
  const desktop = readDesktopCredential();
  if (desktop?.status === "ok") return desktop.value.refreshToken ?? null; // null: legacy credential may have no refresh token
  try {
    if (typeof localStorage === "undefined") return null;
    const token = localStorage.getItem(REFRESH_TOKEN_KEY);
    return token && token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

// CYBORG-1187: bumped by every clear (sign-out goes through clearSavedSession ->
// here). A refresh that started before the bump must not save its rotated token.
let signOutGeneration = 0;

/** Drop the stored refresh token (the relay rejected it, or on logout). */
export function clearRefreshToken(): void {
  clearDesktopCredential();
  signOutGeneration += 1;
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    // best-effort.
  }
}

/**
 * Does this failure mean "your token is no longer valid" (vs. a network error)?
 * Recognizes the relay's textual signals whichever path surfaced first — the
 * `cyborg:error` "unauthorized" reply or the `4003` socket close. Mirrors the
 * CLI's `isUnauthorizedError`.
 */
export function isUnauthorizedError(err: unknown): boolean {
  if (err == null) return false;
  const message = err instanceof Error ? err.message : String(err);
  return /\bunauthorized\b|token required|\b4003\b/i.test(message);
}

/**
 * Derive the HTTP(S) origin the refresh call must hit from the guest WS url the
 * client actually connected to. Self-hosted-safe: the refresh goes to the SAME
 * host, never a hardcoded prod relay. `wss://host/api/ws` → `https://host`.
 */
export function wsUrlToHttpBase(wsUrl: string): string {
  const u = new URL(wsUrl);
  const proto = u.protocol === "wss:" || u.protocol === "https:" ? "https:" : "http:";
  return `${proto}//${u.host}`;
}

export type RefreshOutcome =
  // A fresh access token was minted — reconnect + re-auth with it.
  | { status: "refreshed"; token: string }
  // No usable refresh token (absent) or the relay rejected it (401/403) → the
  // stored token was cleared; surface an actionable re-login.
  | { status: "expired" }
  // Transient failure (network / timeout / 5xx) → the refresh token is kept;
  // the caller lets the normal backoff retry (a later 4003 re-triggers this).
  | { status: "error" };

export interface RefreshDeps {
  fetchFn: typeof fetch;
  load: () => string | null;
  save: (token: string) => void;
  clear: () => void;
  timeoutMs: number;
}

function defaultDeps(): RefreshDeps {
  return {
    fetchFn: (...args: Parameters<typeof fetch>) => fetch(...args),
    load: loadRefreshToken,
    save: saveRefreshToken,
    clear: clearRefreshToken,
    timeoutMs: 10_000,
  };
}

/**
 * Trade the stored refresh token for a fresh access token via
 * `POST {base}/api/auth/refresh`. Returns a discriminated outcome so the caller
 * can distinguish "reconnect with this new token" from "go to re-login" from
 * "transient, retry later". Never throws.
 *
 * - No stored refresh token → `expired` (NO network call — straight to re-login).
 * - 401/403 → the refresh token itself is dead → clear it → `expired`.
 * - Network / timeout / 5xx / malformed body → `error` (token kept).
 * - 200 with a token → persist the rotated refresh token (sliding window) →
 *   `refreshed`.
 */
export async function refreshAccessToken(
  base: string,
  deps: Partial<RefreshDeps> = {},
): Promise<RefreshOutcome> {
  const d = { ...defaultDeps(), ...deps };
  const generation = signOutGeneration;
  const refreshToken = d.load();
  if (!refreshToken) return { status: "expired" };

  let resp: Response;
  try {
    resp = await d.fetchFn(`${base.replace(/\/+$/, "")}/api/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
      // Bound the refresh so a hung relay can't leave the app in limbo.
      signal: timeoutSignal(d.timeoutMs),
    });
  } catch {
    // Network error / timeout / abort — don't discard a possibly-good token.
    return { status: "error" };
  }

  if (!resp.ok) {
    // 401 (invalid/expired refresh) or 403 (suspended) → the durable credential
    // is dead; clear it so we fall straight through to re-login next time.
    if (resp.status === 401 || resp.status === 403) {
      d.clear();
      return { status: "expired" };
    }
    // 5xx / 400 etc — treat as transient, keep the token.
    return { status: "error" };
  }

  let body: { token?: string; refreshToken?: string };
  try {
    body = (await resp.json()) as { token?: string; refreshToken?: string };
  } catch {
    return { status: "error" };
  }
  if (!body.token) return { status: "error" };
  // Signed out while this was in flight: saving would resurrect the session.
  if (signOutGeneration !== generation) return { status: "expired" };
  // Persist the rotated refresh token (sliding window) so every silent re-auth
  // extends the durable session.
  persistDesktopCredential(
    `${base.replace(/\/+$/, "").replace(/^http/, "ws")}/api/ws`,
    body.token,
    body.refreshToken ?? refreshToken,
  );
  if (body.refreshToken) d.save(body.refreshToken);
  return { status: "refreshed", token: body.token };
}
